import type { ReactNode } from 'react';
import { clip } from './scale';
import './charts.css';

export interface CohortRow {
  /** The cohort: "Sep 8", "W37". */
  label: string;
  /** How many started in it. */
  size: number;
  /** Share active (0..1) at each later period; undefined where the period has not happened yet. */
  shares: readonly (number | undefined)[];
  href?: string;
}

export interface CohortGridProps {
  rows: readonly CohortRow[];
  /** Column headings: "Week 1", "Week 2" … */
  periods: readonly string[];
  label: string;
  renderLink?: (href: string, children: ReactNode) => ReactNode;
  cell?: number;
}

const LABEL_W = 96;
const SIZE_W = 40;
/** What fits in the label column at 11px: "Aug 25, 2026" does, a longer name is clipped. */
const LABEL_CHARS = 13;
const HEAD_H = 20;

function shade(share: number): string {
  // Five steps of the accent, from a whisper to solid; text flips to light past the middle.
  const level = Math.min(4, Math.floor(share * 5));
  return `color-mix(in oklch, var(--paige-accent) ${[10, 30, 50, 72, 100][level]}%, var(--paige-surface))`;
}

function Cell({
  share,
  x,
  y,
  size,
}: {
  share: number | undefined;
  x: number;
  y: number;
  size: number;
}): ReactNode {
  if (share === undefined) return null;
  const dark = share >= 0.6;
  return (
    <g
      className={`pui-chart__cell${dark ? ' pui-chart__cell--dark' : ''}`}
      transform={`translate(${x} ${y})`}
    >
      <rect width={size} height={size} fill={shade(share)} />
      <text x={size / 2} y={size / 2 + 3} textAnchor="middle">
        {Math.round(share * 100)}%
      </text>
    </g>
  );
}

/**
 * Rows are cohorts, columns are periods since; the shade is the share still active. Nothing for
 * no rows: the screen says why in words. With links inside, the SVG is a group so a screen reader
 * reaches each cohort; without, one picture with a spoken label.
 */
export function CohortGrid({
  rows,
  periods,
  label,
  renderLink,
  cell = 34,
}: CohortGridProps): ReactNode {
  if (rows.length === 0 || periods.length === 0) return null;
  const width = LABEL_W + SIZE_W + periods.length * cell;
  const height = HEAD_H + rows.length * cell;
  const linked = renderLink !== undefined && rows.some((r) => r.href);
  return (
    <svg
      className="pui-chart"
      viewBox={`0 0 ${width} ${height}`}
      role={linked ? 'group' : 'img'}
      aria-label={label}
    >
      {periods.map((p, i) => (
        <text key={p} x={LABEL_W + SIZE_W + i * cell + cell / 2} y={HEAD_H - 6} textAnchor="middle">
          {p}
        </text>
      ))}
      {rows.map((row, r) => {
        const line = <Row key={row.label} row={row} y={HEAD_H + r * cell} cell={cell} />;
        return row.href && renderLink ? <g key={row.label}>{renderLink(row.href, line)}</g> : line;
      })}
    </svg>
  );
}

/** One cohort: its name, its size, and a shaded cell per period since. */
function Row({ row, y, cell }: { row: CohortRow; y: number; cell: number }): ReactNode {
  const clipped = clip(row.label, LABEL_CHARS);
  return (
    <g>
      <text className="pui-chart__label" x={LABEL_W - 8} y={y + cell / 2 + 4} textAnchor="end">
        {clipped !== row.label ? <title>{row.label}</title> : null}
        {clipped}
      </text>
      <text x={LABEL_W + SIZE_W - 8} y={y + cell / 2 + 4} textAnchor="end">
        {row.size}
      </text>
      {row.shares.map((s, c) => (
        <Cell key={c} share={s} x={LABEL_W + SIZE_W + c * cell} y={y} size={cell} />
      ))}
    </g>
  );
}
